import { useEffect, useState } from 'react';
import { Navigate, Outlet, useParams } from 'react-router';
import { useOrgStore } from '@/stores/org-store';
import { resolveOrgSwitch } from '@/utils/org-switch';
import { useNotification } from '@/components/notifications';
import { Loading } from '@/loading';
import { Button } from '@/components/ui/button';

/**
 * Layout route guard that ensures the orgId in the URL matches the active org
 * in the store. Handles auto-switching for users with access to the URL org.
 *
 * Placed at the `/o/:orgId` route level — all org-scoped routes are nested inside.
 */
const OrgSyncGuard = () => {
  const { orgId } = useParams<{ orgId: string }>();
  const { currentOrg, orgs, setCurrentOrg } = useOrgStore();
  const notification = useNotification();
  const [syncState, setSyncState] = useState<
    'checking' | 'synced' | 'no-access' | 'sso-blocked'
  >('checking');

  useEffect(() => {
    if (!orgId || !currentOrg) {
      setSyncState('checking');
      return;
    }

    const resolution = resolveOrgSwitch(orgs, currentOrg, orgId);

    if (resolution.status === 'sso-blocked') {
      notification.warning(
        `You're signed in with SSO for ${currentOrg.name}. Cannot switch to ${resolution.org.name}.`,
      );
    }

    if (resolution.status !== 'switch') {
      setSyncState(resolution.status);
      return;
    }

    // Auto-switch: update store and localStorage, then reload to clear state
    setCurrentOrg(resolution.org);
    localStorage.setItem('currentOrg', JSON.stringify(resolution.org));
    notification.success(`Switched to ${resolution.org.name}`);
    window.location.reload();
  }, [orgId, currentOrg, orgs, setCurrentOrg, notification]);

  if (syncState === 'checking') {
    return <Loading />;
  }

  if (syncState === 'no-access') {
    return (
      <div className='flex h-screen w-full items-center justify-center'>
        <div className='text-center'>
          <h1 className='text-2xl font-semibold'>Organization not found</h1>
          <p className='text-muted-foreground mt-2'>
            You don&apos;t have access to this organization, or it doesn&apos;t
            exist.
          </p>
          <Button className='mt-4' asChild>
            <a href={currentOrg ? `/o/${currentOrg.orgid}` : '/'}>
              Go to main page
            </a>
          </Button>
        </div>
      </div>
    );
  }

  if (syncState === 'sso-blocked') {
    return <Navigate to={`/o/${currentOrg?.orgid}`} replace />;
  }

  return <Outlet />;
};

export default OrgSyncGuard;
